import React from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Badge,
  Box,
} from "@chakra-ui/react";
import { CherryHubAdminApi } from "../../api/adminApiModels";

interface ExtendedFieldConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  matchingFields: CherryHubAdminApi.ExtendedFieldDefinition[];
  isLoading?: boolean;
}

export const ExtendedFieldConflictModal = ({
  isOpen,
  onClose,
  onContinue,
  matchingFields,
  isLoading = false,
}: ExtendedFieldConflictModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Extended Field Conflict Detected</ModalHeader>
        <ModalBody>
          <VStack alignItems="flex-start" spacing={4} w="100%">
            {matchingFields.length > 0 ? (
              <>
                <Text>
                  The following extended field/s will have a conflicting ID with
                  the field you are trying to create:
                </Text>

                <VStack
                  alignItems="flex-start"
                  spacing={2}
                  w="100%"
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  maxH="300px"
                  overflowY="auto"
                >
                  {matchingFields.map((field) => (
                    <HStack
                      key={field.id}
                      w="100%"
                      justifyContent="space-between"
                      p={2}
                      bg="white"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Box flex={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          fontFamily="monospace"
                        >
                          {field.id}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          Field Name: {field.FieldName}
                        </Text>
                      </Box>
                      <Badge
                        colorScheme={field.Meta?.IsDeleted ? "red" : "green"}
                      >
                        {field.Meta?.IsDeleted ? "Deleted" : "Active"}
                      </Badge>
                    </HStack>
                  ))}
                </VStack>
              </>
            ) : (
              <Text>
                A conflict was detected with an existing extended field that has
                the same field name, owner, and field type.
              </Text>
            )}

            <Text>
              If you choose to continue, a new field will be created with an
              incremented version number. If you wish to re-use any existing
              field that has been deleted, click cancel and then proceed to
              restore the field.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="cherryButton"
              onClick={onContinue}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Continue
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
